import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Ban, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { TREASURY_STATUS_BADGE_VARIANT } from "@/components/treasury/format";
import {
  usePaymentVoucher,
  useVoidPaymentVoucher,
} from "@/lib/api/payment-vouchers.queries";
import { translateApiError } from "@/lib/translate-api-error";
import { useActiveCompany } from "@/lib/company-context";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";

export const Route = createFileRoute(
  "/companies/$companyId/egresos/$voucherId",
)({
  component: PaymentVoucherDetailPage,
});

function PaymentVoucherDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PaymentVoucherDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function PaymentVoucherDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, voucherId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const detailQuery = usePaymentVoucher(companyId, voucherId);
  const voidMutation = useVoidPaymentVoucher(companyId);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("vouchers.detail.error_title")}
        description={t("vouchers.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const voucher = detailQuery.data;

  async function handleVoid() {
    if (!voidReason.trim()) {
      toast.error(t("treasury.void.reason_required"));
      return;
    }
    try {
      await voidMutation.mutateAsync({ voucherId, reason: voidReason.trim() });
      toast.success(t("vouchers.toast.voided"));
      setVoidOpen(false);
      setVoidReason("");
    } catch (err) {
      toast.error(translateApiError(err, t, "vouchers.error"));
    }
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/egresos"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("vouchers.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              CE-{voucher.voucherNumber}
            </h1>
            <Badge variant={TREASURY_STATUS_BADGE_VARIANT[voucher.status]}>
              {t(`treasury.status.${voucher.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {voucher.terceroName ?? voucher.terceroId} · {voucher.paymentDate} ·{" "}
            {t(`treasury.method.${voucher.paymentMethod}`)}
          </p>
        </div>
        {voucher.status === "posted" ? (
          <Button
            variant="destructive"
            onClick={() => setVoidOpen(true)}
            disabled={voidMutation.isPending}
          >
            <Ban className="h-4 w-4" />
            {t("vouchers.actions.void")}
          </Button>
        ) : null}
      </div>

      {voucher.status === "void" && voucher.voidReason ? (
        <Card className="border-destructive/40">
          <CardContent className="pt-6 text-sm">
            <span className="font-medium">{t("vouchers.detail.void_reason")}:</span>{" "}
            {voucher.voidReason}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("vouchers.detail.amounts")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label={t("vouchers.balance.paid")}
              value={formatCop(voucher.amountPaid)}
            />
            {voucher.discountAmount > 0 ? (
              <Row
                label={t("vouchers.form.discount")}
                value={formatCop(voucher.discountAmount)}
              />
            ) : null}
            <div className="border-t pt-2">
              <Row
                label={t("vouchers.balance.total_to_apply")}
                value={formatCop(voucher.totalApplied)}
                bold
              />
            </div>
            {voucher.concept ? (
              <p className="pt-2 text-sm">
                <span className="text-muted-foreground">
                  {t("vouchers.form.concept")}:
                </span>{" "}
                {voucher.concept}
              </p>
            ) : null}
            {voucher.reference ? (
              <p className="text-xs text-muted-foreground">
                {t("vouchers.form.reference")}: {voucher.reference}
              </p>
            ) : null}
            {voucher.journalEntryId ? (
              <Link
                to="/companies/$companyId/contabilidad/diario/$entryId"
                params={{ companyId, entryId: voucher.journalEntryId }}
                className="inline-block pt-2 text-xs text-primary hover:underline"
              >
                {t("vouchers.detail.journal_link")}
              </Link>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("vouchers.detail.applications")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {voucher.applications.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("vouchers.detail.no_applications")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("vouchers.detail.document")}</TableHead>
                    <TableHead>{t("vouchers.detail.target_type")}</TableHead>
                    <TableHead className="text-right">
                      {t("vouchers.form.applied_amount")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.applications.map((application) => (
                    <TableRow key={application.id}>
                      <TableCell>
                        {application.purchaseInvoiceId ? (
                          <Link
                            to="/companies/$companyId/compras/$invoiceId"
                            params={{
                              companyId,
                              invoiceId: application.purchaseInvoiceId,
                            }}
                            className="font-mono text-sm text-primary hover:underline"
                          >
                            {application.invoiceNumber}
                          </Link>
                        ) : (
                          <span className="font-mono text-sm">
                            {application.invoiceNumber}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted" className="text-[10px]">
                          {application.targetType === "support_document"
                            ? t("vouchers.form.target_ds")
                            : t("vouchers.form.target_pi")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(application.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {voucher.notes ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("vouchers.detail.notes")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">{voucher.notes}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="payment_vouchers"
            documentId={voucherId}
          />
        </CardContent>
      </Card>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vouchers.void.title")}</DialogTitle>
            <DialogDescription>{t("vouchers.void.body")}</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="voucher-void-reason">
              {t("treasury.void.reason")}
            </Label>
            <textarea
              id="voucher-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t("treasury.void.reason_placeholder")}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={voidMutation.isPending || !voidReason.trim()}
            >
              {t("vouchers.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
